/**
 * 分类商品列表页 - 中式典雅风格
 * 配色：宣纸白 (#F8F6F1)、茶褐 (#7D6B55)、竹青 (#6B8E7C)、朱砂红 (#C44536)
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Row,
  Col,
  Card,
  Button,
  Typography,
  Spin,
  Pagination,
  Image,
  Breadcrumb,
  Empty,
  Grid,
} from 'antd';
import { HomeOutlined, ShoppingOutlined } from '@ant-design/icons';
import StorefrontLayout from '../../components/storefront/StorefrontLayout';
import { productApi } from '../../api/index';

const { Title, Text } = Typography;

// 中式配色
const COLORS = {
  paper: '#F8F6F1',
  teaBrown: '#7D6B55',
  bambooGreen: '#6B8E7C',
  cinnabar: '#C44536',
  ink: '#2C2C2C',
  lightInk: '#5A5A5A',
  lightBorder: '#E0D8D0',
};

export default function CategoryPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const pageSize = 20;

  useEffect(() => {
    setPage(1);
    fetchCategory(slug);
  }, [slug]);

  useEffect(() => {
    if (category?.id) {
      fetchProducts(category.id, page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, category]);

  const fetchCategory = async (slug) => {
    try {
      const data = await productApi.getCategoryBySlug(slug);
      setCategory(data);
    } catch {
      setCategory({ id: null, name: slug, slug });
    }
  };

  const fetchProducts = async (categoryId, currentPage) => {
    if (!categoryId) return;
    setLoading(true);
    try {
      const data = await productApi.list({
        categoryId,
        page: currentPage,
        pageSize,
      });
      if (Array.isArray(data)) {
        setProducts(data);
        setTotal(data.length);
      } else if (data?.items) {
        setProducts(data.items);
        setTotal(data.total || 0);
      } else if (data?.list) {
        setProducts(data.list);
        setTotal(data.total || 0);
      } else {
        setProducts([]);
        setTotal(0);
      }
    } catch {
      setProducts([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (product) => {
    if (!product.variants || product.variants.length === 0) return '0.00';
    const minPrice = Math.min(...product.variants.map((v) => v.price || 0));
    return minPrice.toFixed(2);
  };

  const getProductImage = (product) => {
    if (product.images && product.images.length > 0) {
      return product.images[0].url;
    }
    return null;
  };

  return (
    <StorefrontLayout>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 24px 48px' }}>
        {/* 美化面包屑 */}
        <Breadcrumb
          separator={<span style={{ color: `${COLORS.teaBrown}30`, margin: '0 8px' }}> / </span>}
          items={[
            {
              href: '/',
              title: (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    color: COLORS.teaBrown,
                    transition: 'color 0.2s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = COLORS.cinnabar)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = COLORS.teaBrown)}
                >
                  <HomeOutlined />
                  <span style={{ marginLeft: 4 }}>首页</span>
                </span>
              ),
            },
            {
              title: (
                <span style={{ color: COLORS.lightInk }}>
                  分类
                </span>
              ),
            },
            {
              title: (
                <span style={{ color: COLORS.ink, fontWeight: 500 }}>
                  {category?.name || slug}
                </span>
              ),
            },
          ]}
          style={{
            marginBottom: isMobile ? 16 : 24,
            padding: isMobile ? '12px 16px' : '16px 20px',
            background: `${COLORS.teaBrown}05`,
            borderRadius: 8,
            border: `1px solid ${COLORS.teaBrown}10`,
          }}
        />

        {/* 分类标题 */}
        <div style={{ marginBottom: 32, textAlign: 'center' }}>
          <Title
            level={2}
            style={{
              color: COLORS.ink,
              fontSize: 26,
              fontWeight: 400,
              letterSpacing: '0.15em',
              marginBottom: 8,
            }}
          >
            {category?.name || slug}
          </Title>
          <div
            style={{
              width: 50,
              height: 2,
              background: COLORS.cinnabar,
              margin: '0 auto',
            }}
          />
        </div>

        {/* 商品网格 */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 80 }}>
            <Spin size="large" />
          </div>
        ) : products.length === 0 ? (
          <Empty
            description={<Text style={{ color: COLORS.lightInk }}>暂无商品</Text>}
          >
            <Button
              onClick={() => navigate('/products')}
              style={{
                borderRadius: 8,
                borderColor: COLORS.teaBrown,
                color: COLORS.teaBrown,
              }}
            >
              查看全部商品
            </Button>
          </Empty>
        ) : (
          <>
            <Row gutter={[32, 32]}>
              {products.map((product) => {
                const imgUrl = getProductImage(product);
                return (
                  <Col key={product.id} xs={24} sm={12} md={8} lg={6}>
                    <Card
                      hoverable
                      onClick={() => navigate(`/products/${product.slug}`)}
                      cover={
                        <div style={{ height: 260, overflow: 'hidden', background: `${COLORS.teaBrown}08` }}>
                          {imgUrl ? (
                            <Image
                              src={imgUrl}
                              alt={product.title}
                              height={260}
                              width="100%"
                              style={{ objectFit: 'cover' }}
                              preview={false}
                            />
                          ) : (
                            <div
                              style={{
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <ShoppingOutlined
                                style={{ fontSize: 56, color: `${COLORS.teaBrown}30` }}
                              />
                            </div>
                          )}
                        </div>
                      }
                      bodyStyle={{ padding: '14px' }}
                      style={{
                        border: 'none',
                        borderRadius: 10,
                        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                        transition: 'transform 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <Text
                        strong
                        ellipsis={{ rows: 2 }}
                        style={{
                          display: 'block',
                          fontSize: 14,
                          color: COLORS.ink,
                          marginBottom: 8,
                          lineHeight: 1.4,
                        }}
                      >
                        {product.title}
                      </Text>
                      <Text
                        style={{
                          color: COLORS.cinnabar,
                          fontSize: 16,
                          fontWeight: 600,
                        }}
                      >
                        ${formatPrice(product)}
                      </Text>
                    </Card>
                  </Col>
                );
              })}
            </Row>

            {/* 分页 */}
            {total > pageSize && (
              <Pagination
                current={page}
                total={total}
                pageSize={pageSize}
                onChange={(p) => setPage(p)}
                showSizeChanger={false}
                style={{
                  textAlign: 'center',
                  marginTop: 48,
                  '--ant-pagination-item-border-radius': '6px',
                }}
                itemRender={(page, type, originalElement) => {
                  if (type === 'page') {
                    return (
                      <span
                        style={{
                          minWidth: 32,
                          height: 32,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 6,
                          border: `1px solid ${page === page ? COLORS.teaBrown : COLORS.lightBorder}`,
                          color: page === page ? COLORS.teaBrown : COLORS.lightInk,
                          background: page === page ? `${COLORS.teaBrown}08` : 'transparent',
                          cursor: 'pointer',
                        }}
                      >
                        {page}
                      </span>
                    );
                  }
                  return originalElement;
                }}
              />
            )}
          </>
        )}
      </div>
    </StorefrontLayout>
  );
}
